import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE, fmtINR, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, FileText, X, Plus, Check, Loader2, Minus, ArrowLeft, Printer as PrinterIcon, ArrowRight, CreditCard, Wallet, Trash2 } from "lucide-react";

const STEPS = ["Upload", "Options", "Pay", "Track"];

const PublicApi = (path) => `${API_BASE}${path}`;

export default function PublicStore() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [store, setStore] = useState(null);
  const [files, setFiles] = useState([]);
  const [configs, setConfigs] = useState({});
  const [applyAll, setApplyAll] = useState({ color_mode: "bw", paper_size: "A4", duplex: false, copies: 1, orientation: "auto" });
  const [step, setStep] = useState(0);
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [uploading, setUploading] = useState(false);
  const [paying, setPaying] = useState(false);
  // Default to cash-first (shops are cash-only unless online is explicitly enabled).
  const [payMethod, setPayMethod] = useState("cash"); // "online" | "cash"
  const [customer, setCustomer] = useState({ name: "", mobile: "" });
  const [deletingFiles, setDeletingFiles] = useState(false);
  const [filesDeleted, setFilesDeleted] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    axios.get(`${API_BASE}/public/store/${slug}`).then((r) => {
      setStore(r.data);
      // Auto-select whichever payment method the shop enables (cash > online preference).
      if (r.data?.accept_cash) setPayMethod("cash");
      else if (r.data?.accept_online) setPayMethod("online");
    }).catch(() => setStore(false));
  }, [slug]);

  const configList = files.map((f) => ({
    file_id: f.id,
    ...(configs[f.id] || applyAll),
  }));

  useEffect(() => {
    if (files.length === 0) { setQuote(null); return; }
    axios.post(`${API_BASE}/public/store/${slug}/quote`, { file_configs: configList, coupon_code: couponCode || null })
      .then((r) => setQuote(r.data)).catch(() => {});
  }, [files, configs, applyAll, slug, couponCode]);

  // poll status
  useEffect(() => {
    if (step !== 3 || !order?.job_number) return;
    const t = setInterval(async () => {
      try { const { data } = await axios.get(`${API_BASE}/public/track/${order.job_number}`); setStatus(data); } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [step, order]);

  if (store === null) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;
  if (store === false) return <div className="min-h-screen grid place-items-center paper-bg"><Card><CardContent className="p-8 text-center"><h2 className="font-display text-xl font-bold">Store not found</h2></CardContent></Card></div>;
  if (store.status !== "active") return <ExpiredPage title={`${store.name}`} message="This store is not accepting orders right now." />;
  if (!store.subscription_active) return <ExpiredPage title={store.name} message="Please renew the subscription." />;

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${API_BASE}/public/store/${slug}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      } catch (err) { toast.error(`${file.name}: ${formatError(err)}`); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = async (id) => {
    const target = files.find((f) => f.id === id);
    // Optimistic UI: drop it from the list first so the customer sees instant feedback.
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setConfigs((prev) => { const c = { ...prev }; delete c[id]; return c; });
    try {
      await axios.delete(`${API_BASE}/public/store/${slug}/files/${id}`);
      toast.success(`Removed ${target?.filename || "file"} from Print Plus`);
    } catch (err) {
      // Server refused (e.g. file already attached to an active job) — put it back so the customer isn't confused.
      if (target) setFiles((prev) => [...prev, target]);
      toast.error(err?.response?.data?.detail || "Couldn't remove that file — please try again");
    }
  };

  const setCopies = (id, n) => {
    const base = configs[id] || applyAll;
    setConfigs({ ...configs, [id]: { ...base, copies: Math.max(1, n) } });
  };

  const pay = async () => {
    if (payMethod === "cash") {
      if (!customer.name.trim()) {
        toast.error("Please enter your name so the shop can identify you at the counter.");
        return;
      }
      if (customer.mobile && customer.mobile.length > 0 && customer.mobile.length < 10) {
        toast.error("Mobile number must be 10 digits, or leave it blank.");
        return;
      }
      setPaying(true);
      try {
        const { data } = await axios.post(`${API_BASE}/public/store/${slug}/create-cash-order`, {
          file_configs: configList, coupon_code: couponCode || null,
          customer_name: customer.name, customer_mobile: customer.mobile,
        });
        setOrder(data);
        setStep(3);
        setStatus({ status: "pending_approval" });
        toast.success("Order placed — pay cash at the counter");
      } catch (err) {
        toast.error(formatError(err));
      } finally {
        setPaying(false);
      }
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/store/${slug}/create-order`, { file_configs: configList, coupon_code: couponCode || null });
      // Load Razorpay checkout.js if not already loaded
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = resolve;
          s.onerror = () => reject(new Error("Failed to load Razorpay"));
          document.body.appendChild(s);
        });
      }
      const rzp = new window.Razorpay({
        key: data.razorpay_key,
        amount: data.amount_paise,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: store.name,
        description: `Print job ${data.job_number}`,
        theme: { color: "#F97316" },
        handler: async (res) => {
          try {
            const verify = await axios.post(`${API_BASE}/public/verify-payment`, {
              order_id: data.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            setOrder(verify.data);
            setStep(3);
            toast.success("Payment successful");
          } catch (err) {
            toast.error(formatError(err));
          } finally { setPaying(false); }
        },
        modal: {
          ondismiss: () => { setPaying(false); toast.info("Payment cancelled"); },
        },
      });
      rzp.on("payment.failed", (resp) => {
        setPaying(false);
        toast.error(`Payment failed: ${resp.error?.description || "Please try again"}`);
      });
      rzp.open();
    } catch (err) {
      toast.error(formatError(err));
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen paper-bg md:py-10">
      <div className="max-w-md mx-auto px-4 pt-5 pb-36 md:bg-white md:rounded-3xl md:shadow-2xl md:border md:border-slate-200 md:px-8 md:pt-8 md:pb-12">
        {/* Compact header: logo + name + status on one row */}
        <header className="flex items-center gap-3 mb-4">
          {store.logo_url
            ? <img src={store.logo_url} alt="" className="w-11 h-11 rounded-xl object-cover flex-shrink-0" />
            : <img src="/logo.png" alt="Print Plus" className="w-11 h-11 object-contain flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-lg font-bold tracking-tight truncate" data-testid="public-store-name">{store.name}</h1>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Online
              </span>
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <span className="truncate">{store.city} · Print in minutes</span>
              <span>·</span>
              <Link to="/track" className="text-orange-600 hover:underline whitespace-nowrap">Track</Link>
            </div>
          </div>
        </header>

        {/* Progress */}
        <div className="flex gap-1 mb-5" data-testid="progress-bar">{STEPS.map((s, i) => <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-orange-500" : "bg-slate-200"}`} />)}</div>

        {step === 0 && (
          <div className="space-y-3 fade-in">
            <input ref={fileRef} type="file" multiple hidden onChange={onFiles} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.ppt,.pptx,.txt" data-testid="file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className={`w-full rounded-2xl border-2 border-dashed border-orange-300 bg-orange-50 text-center hover:bg-orange-100 transition active:scale-[0.99] ${files.length === 0 ? "p-8" : "p-4 flex items-center gap-3"}`} data-testid="upload-btn">
              <div className={`rounded-full bg-orange-500 grid place-items-center flex-shrink-0 ${files.length === 0 ? "w-14 h-14 mx-auto mb-3" : "w-10 h-10"}`}>
                {uploading ? <Loader2 className={`text-white animate-spin ${files.length === 0 ? "w-6 h-6" : "w-5 h-5"}`} /> : <Upload className={`text-white ${files.length === 0 ? "w-6 h-6" : "w-5 h-5"}`} />}
              </div>
              <div className={files.length === 0 ? "" : "text-left flex-1 min-w-0"}>
                <div className={`font-display font-bold ${files.length === 0 ? "text-lg" : "text-sm"}`}>{uploading ? "Uploading…" : files.length === 0 ? "Upload files" : "Add more files"}</div>
                <div className={`text-slate-500 ${files.length === 0 ? "text-xs mt-1" : "text-[11px]"}`}>PDF, DOCX, images and more · up to 25MB</div>
              </div>
            </button>

            {files.map((f) => (
              <div key={f.id} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-200 fade-in" data-testid={`file-card-${f.filename}`}>
                <div className="w-14 h-14 rounded-lg bg-slate-100 grid place-items-center flex-shrink-0 overflow-hidden border border-slate-200">
                  <img
                    src={PublicApi(`/public/files/${f.id}/thumb`)}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "grid"; }}
                    data-testid={`thumb-${f.filename}`}
                  />
                  <div className="w-full h-full hidden place-items-center bg-slate-100"><FileText className="w-5 h-5 text-slate-400" /></div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.filename}</div>
                  <div className="text-xs text-slate-400 font-mono">{f.pages} page{f.pages > 1 ? "s" : ""} · {(f.size / 1024).toFixed(0)}KB</div>
                </div>
                <button onClick={() => removeFile(f.id)} className="w-9 h-9 rounded-full bg-red-50 border border-red-200 grid place-items-center text-red-500 hover:bg-red-100 hover:text-red-600 flex-shrink-0 transition-colors" data-testid={`remove-${f.filename}`} aria-label="Delete this file" title="Delete this file"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3 fade-in" data-testid="options-step">
            <button onClick={() => setStep(0)} className="text-sm text-slate-500 flex items-center gap-1 hover:text-orange-600" data-testid="back-to-upload"><ArrowLeft className="w-4 h-4" />Back</button>
            {files.map((f) => {
              const cfg = configs[f.id] || applyAll;
              const setCfg = (patch) => setConfigs({ ...configs, [f.id]: { ...cfg, ...patch } });
              const rate = cfg.color_mode === "color" ? store.pricing.color_per_page : store.pricing.bw_per_page;
              return (
                <Card key={f.id} className="border-slate-200" data-testid={`file-config-${f.filename}`}>
                  <CardContent className="p-3 space-y-2.5">
                    {/* Row 1: thumb + name + copies stepper */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 grid place-items-center flex-shrink-0 overflow-hidden border border-slate-200">
                        <img src={PublicApi(`/public/files/${f.id}/thumb`)} alt="" className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "grid"; }} />
                        <div className="w-full h-full hidden place-items-center bg-slate-100"><FileText className="w-4 h-4 text-slate-400" /></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{f.filename}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{f.pages} page{f.pages > 1 ? "s" : ""} · ₹{rate}/page</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => setCfg({ copies: Math.max(1, cfg.copies - 1) })} className="w-7 h-7 rounded-lg bg-slate-100 grid place-items-center hover:bg-slate-200" data-testid={`copies-minus-${f.filename}`}><Minus className="w-3 h-3" /></button>
                        <span className="w-6 text-center font-mono font-bold text-sm" data-testid={`copies-${f.filename}`}>{cfg.copies}</span>
                        <button onClick={() => setCfg({ copies: cfg.copies + 1 })} className="w-7 h-7 rounded-lg bg-slate-100 grid place-items-center hover:bg-slate-200" data-testid={`copies-plus-${f.filename}`}><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                    {/* Row 2: colour pills (2) + orientation pills (3) — same row */}
                    <div className="grid grid-cols-5 gap-1">
                      {[["bw", "B&W"], ["color", "Colour"]].map(([v, l]) => (
                        <button key={v} onClick={() => setCfg({ color_mode: v })}
                          className={`py-1.5 rounded-lg text-[11px] font-semibold border transition ${cfg.color_mode === v ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-500"}`}
                          data-testid={`file-mode-${f.filename}-${v}`}>{l}</button>
                      ))}
                      {[["auto", "Auto"], ["portrait", "Portrait"], ["landscape", "Landscape"]].map(([v, l]) => (
                        <button key={v} onClick={() => setCfg({ orientation: v })}
                          className={`py-1.5 rounded-lg text-[11px] font-medium border transition ${cfg.orientation === v ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-500"}`}
                          data-testid={`file-orientation-${f.filename}-${v}`}>{l}</button>
                      ))}
                    </div>
                    {/* Row 3: duplex (if supported) + page range (if multi-page) */}
                    {(store.printer_duplex || f.pages > 1) && (
                      <div className="flex items-center gap-2">
                        {store.printer_duplex && (
                          <button onClick={() => setCfg({ duplex: !cfg.duplex })}
                            className={`flex-shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition ${cfg.duplex ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-500"}`}
                            data-testid={`file-duplex-${f.filename}`}>
                            {cfg.duplex ? "✓ Duplex" : "Duplex"}
                          </button>
                        )}
                        {f.pages > 1 && (
                          <Input
                            placeholder={`Pages (all ${f.pages})`}
                            value={cfg.page_range || ""}
                            onChange={(e) => setCfg({ page_range: e.target.value })}
                            className="h-8 text-[11px] font-mono"
                            data-testid={`file-page-range-${f.filename}`}
                          />
                        )}
                      </div>
                    )}
                    {f.pages > 1 && cfg.page_range && (
                      <div className="text-[10px] text-slate-400 font-mono">e.g. 1-3, 5, 7-9 · empty = print all pages</div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {step === 2 && quote && (
          <div className="space-y-3 fade-in">
            <button onClick={() => setStep(1)} className="text-sm text-slate-500 flex items-center gap-1 hover:text-orange-600"><ArrowLeft className="w-4 h-4" />Back</button>
            <Card className="border-slate-200"><CardContent className="p-4 space-y-2">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Order summary</div>
              {quote.breakdown.map((b, i) => {
                const cfg = configList[i] || {};
                return (
                <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                  <div className="truncate flex-1">
                    <div className="truncate">{b.filename}</div>
                    <div className="text-xs text-slate-400 font-mono">{b.total_pages}p × ₹{b.rate} · {b.color_mode} · {cfg.orientation || "auto"}{cfg.duplex ? " · duplex" : ""}</div>
                  </div>
                  <div className="font-mono">{fmtINR(b.amount)}</div>
                </div>
              );})}
              <div className="pt-2 space-y-1 text-sm">
                <div className="flex justify-between text-slate-500"><span>B&W pages</span><span className="font-mono">{quote.bw_pages}</span></div>
                <div className="flex justify-between text-slate-500"><span>Colour pages</span><span className="font-mono">{quote.color_pages}</span></div>
                <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="font-mono">{fmtINR(quote.subtotal)}</span></div>
                {quote.coupon && <div className="flex justify-between text-emerald-600 font-medium"><span>Coupon {quote.coupon.code}</span><span className="font-mono">−{fmtINR(quote.discount)}</span></div>}
                {quote.tax > 0 && <div className="flex justify-between text-slate-500"><span>GST</span><span className="font-mono">{fmtINR(quote.tax)}</span></div>}
                <div className="flex justify-between font-display font-bold text-lg pt-2"><span>Total</span><span className="font-mono text-orange-600" data-testid="total-amount">{fmtINR(quote.total)}</span></div>
              </div>
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4 space-y-2">
              <Label className="text-xs">Coupon code</Label>
              <div className="flex gap-2">
                <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="e.g. LAUNCH50" data-testid="coupon-input" />
                {quote?.coupon ? <Badge className="bg-emerald-500 self-center">Applied</Badge> : null}
              </div>
              {couponCode && !quote?.coupon && <div className="text-xs text-red-500">Code not valid</div>}
            </CardContent></Card>

            {/* Payment method selector — shows only the methods the shop has enabled */}
            <Card className="border-slate-200" data-testid="payment-method-card"><CardContent className="p-4 space-y-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Payment method</div>
              {!store.accept_online && !store.accept_cash && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  This shop is not accepting any online or counter payments right now. Please contact the shop directly.
                </div>
              )}
              <div className={`grid gap-2 ${(store.accept_online && store.accept_cash) ? "grid-cols-2" : "grid-cols-1"}`}>
                {store.accept_cash && (
                  <button onClick={() => setPayMethod("cash")}
                    className={`p-3 rounded-xl border text-left transition ${payMethod === "cash" ? "border-orange-500 bg-orange-50" : "border-slate-200 bg-white"}`}
                    data-testid="pay-method-cash">
                    <div className="flex items-center gap-2 mb-1"><Wallet className={`w-4 h-4 ${payMethod === "cash" ? "text-orange-600" : "text-slate-400"}`} /><div className="font-semibold text-sm">Pay at counter</div></div>
                    <div className="text-[11px] text-slate-500">Cash to the shop · prints after approval</div>
                  </button>
                )}
                {store.accept_online && (
                  <button onClick={() => setPayMethod("online")}
                    className={`p-3 rounded-xl border text-left transition ${payMethod === "online" ? "border-orange-500 bg-orange-50" : "border-slate-200 bg-white"}`}
                    data-testid="pay-method-online">
                    <div className="flex items-center gap-2 mb-1"><CreditCard className={`w-4 h-4 ${payMethod === "online" ? "text-orange-600" : "text-slate-400"}`} /><div className="font-semibold text-sm">Pay online</div></div>
                    <div className="text-[11px] text-slate-500">UPI, cards, netbanking · instant print</div>
                  </button>
                )}
              </div>
              {payMethod === "cash" && (
                <div className="space-y-2 pt-1 border-t border-slate-100" data-testid="cash-details-form">
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    Your job will wait as <span className="font-mono">Pending approval</span>. The shop only prints after you hand over the cash.
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Your name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="cash-name" />
                    <Input placeholder="Mobile (optional)" inputMode="tel" value={customer.mobile} onChange={(e) => setCustomer({ ...customer, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid="cash-mobile" />
                  </div>
                  <div className="text-[10px] text-slate-400">Mobile is optional but helps the shop reach you if your print is ready.</div>
                </div>
              )}
            </CardContent></Card>
          </div>
        )}

        {step === 3 && order && (
          <div className="space-y-4 fade-in text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 grid place-items-center mx-auto"><Check className="w-10 h-10 text-emerald-500" /></div>
            <h2 className="font-display text-2xl font-bold">Payment successful!</h2>
            <p className="text-slate-500">Your job is on the way to the printer.</p>
            <Card className="border-slate-200"><CardContent className="p-4 space-y-2">
              <div className="text-xs uppercase tracking-widest text-slate-500">Job ID</div>
              <div className="font-mono text-xl font-bold" data-testid="job-number">{order.job_number}</div>
              <div className="pt-3">
                <StatusPill status={status?.status || "queued"} />
              </div>
            </CardContent></Card>

            {/* Delete files card — only visible once the print is done */}
            {status?.status === "printed" && !filesDeleted && (
              <Card className="border-slate-200 bg-white text-left" data-testid="post-print-delete-card">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-900 text-white grid place-items-center shrink-0"><Trash2 className="w-4 h-4" /></div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-slate-900">Your print is done</div>
                      <div className="text-xs text-slate-500 mt-1">Permanently remove the uploaded document{files.length === 1 ? "" : "s"} from Print Plus for extra privacy.</div>
                    </div>
                  </div>
                  <Button
                    onClick={async () => {
                      if (!window.confirm("Permanently delete your uploaded files from Print Plus? This can't be undone.")) return;
                      setDeletingFiles(true);
                      try {
                        const { data } = await axios.post(`${API_BASE}/public/track/${order.job_number}/delete-files`);
                        toast.success(`Removed ${data.deleted_count || files.length} file${(data.deleted_count || files.length) === 1 ? "" : "s"} from Print Plus`);
                        setFilesDeleted(true);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Couldn't delete files");
                      } finally { setDeletingFiles(false); }
                    }}
                    disabled={deletingFiles}
                    className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                    data-testid="post-print-delete-btn"
                  >
                    {deletingFiles ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    {deletingFiles ? "Deleting…" : "Delete my files from Print Plus"}
                  </Button>
                </CardContent>
              </Card>
            )}
            {filesDeleted && (
              <Card className="border-emerald-200 bg-emerald-50/60 text-left" data-testid="post-print-deleted-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 text-white grid place-items-center shrink-0"><Check className="w-4 h-4" /></div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-slate-900">Files removed</div>
                    <div className="text-xs text-slate-600">Your uploaded documents were permanently deleted.</div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button variant="outline" className="w-full" onClick={() => { setFiles([]); setConfigs({}); setStep(0); setOrder(null); setStatus(null); setFilesDeleted(false); }}>Place another order</Button>
          </div>
        )}
      </div>

      {/* Sticky action bar — always visible at bottom on steps 0/1/2 */}
      {step === 0 && files.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white/95 backdrop-blur-md p-3 shadow-lg md:relative md:border-0 md:shadow-none md:bg-transparent md:p-0 md:mt-4 z-30" data-testid="sticky-continue-bar">
          <div className="max-w-md mx-auto flex items-center gap-3 md:px-8">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">{files.length} file{files.length > 1 ? "s" : ""} ready</div>
              <div className="text-sm font-mono text-slate-800">{files.reduce((sum, f) => sum + (f.pages || 0), 0)} pages total</div>
            </div>
            <Button className="bg-orange-500 hover:bg-orange-600 h-12 px-5 font-semibold" onClick={() => setStep(1)} data-testid="continue-to-options">
              Continue<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white/95 backdrop-blur-md p-3 shadow-lg md:relative md:border-0 md:shadow-none md:bg-transparent md:p-0 md:mt-4 z-30" data-testid="sticky-review-bar">
          <div className="max-w-md mx-auto flex items-center gap-3 md:px-8">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">Running total</div>
              <div className="font-display font-bold text-lg text-orange-600 font-mono">{quote ? fmtINR(quote.total) : "…"}</div>
            </div>
            <Button className="bg-orange-500 hover:bg-orange-600 h-12 px-5 font-semibold" onClick={() => setStep(2)} data-testid="continue-to-pay">
              Review & Pay<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {step === 2 && quote && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white/95 backdrop-blur-md p-3 shadow-lg md:relative md:border-0 md:shadow-none md:bg-transparent md:p-0 md:mt-4 z-30">
          <div className="max-w-md mx-auto flex items-center gap-3 md:px-8">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">Total</div>
              <div className="font-display font-bold text-xl text-orange-600 font-mono">{fmtINR(quote.total)}</div>
            </div>
            <Button disabled={paying} onClick={pay} className="bg-orange-500 hover:bg-orange-600 h-12 px-5 font-semibold" data-testid="pay-btn">
              {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (payMethod === "cash" ? <Wallet className="w-4 h-4 mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />)}
              {paying ? "Processing…" : payMethod === "cash" ? "Place cash order" : "Pay now"}
              {!paying && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    queued: { label: "Queued at printer", color: "bg-blue-500" },
    printing: { label: "Printing now…", color: "bg-amber-500 pulse-ring" },
    printed: { label: "Printed! Please collect.", color: "bg-emerald-500" },
    failed: { label: "Failed — please contact shop", color: "bg-red-500" },
    payment_pending: { label: "Awaiting payment", color: "bg-slate-400" },
  };
  const m = map[status] || map.queued;
  return <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-medium" style={{}}><span className={`w-2 h-2 rounded-full ${m.color}`} /><span className="text-slate-700">{m.label}</span></div>;
}

function ExpiredPage({ title, message }) {
  return (
    <div className="min-h-screen paper-bg grid place-items-center p-6">
      <Card className="max-w-sm border-slate-200"><CardContent className="p-8 text-center space-y-3">
        <div className="w-14 h-14 rounded-full bg-red-50 grid place-items-center mx-auto"><PrinterIcon className="w-7 h-7 text-red-500" /></div>
        <h2 className="font-display text-2xl font-bold">{title}</h2>
        <p className="text-slate-500">{message}</p>
      </CardContent></Card>
    </div>
  );
}
